import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, ImagePlus, Loader2, Info, X, Lock } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

const GENERATE_VIDEO_URL = 'https://ai-agency-dt7d.onrender.com/webhook/generar_video';

type ModeloMode = 'ia' | 'guardados' | 'personalizado';

const accentOptions = [
  { value: 'argentino', label: 'Argentino' },
  { value: 'chileno', label: 'Chileno' },
  { value: 'mexicano', label: 'Mexicano' },
  { value: 'colombiano', label: 'Colombiano' },
  { value: 'peruano', label: 'Peruano' },
  { value: 'venezolano', label: 'Venezolano' },
  { value: 'ecuatoriano', label: 'Ecuatoriano' },
  { value: 'boliviano', label: 'Boliviano' },
  { value: 'uruguayo', label: 'Uruguayo' },
  { value: 'paraguayo', label: 'Paraguayo' },
  { value: 'español', label: 'Español' },
  { value: 'cubano', label: 'Cubano' },
  { value: 'dominicano', label: 'Dominicano' },
  { value: 'puertorriqueno', label: 'Puertorriqueño' },
  { value: 'costarricense', label: 'Costarricense' },
  { value: 'panameño', label: 'Panameño' },
  { value: 'guatemalteco', label: 'Guatemalteco' },
  { value: 'hondureño', label: 'Hondureño' },
  { value: 'salvadoreño', label: 'Salvadoreño' },
  { value: 'nicaraguense', label: 'Nicaragüense' },
  { value: 'ingles', label: 'Inglés' },
];

const ConfigurarVideoInfluencer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );
  const [modoPro, setModoPro] = useState<boolean>(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [showProPlanModal, setShowProPlanModal] = useState<boolean>(false);
  const isProPlanAllowed = userPlan === 'negocio' || userPlan === 'enterprise' || userPlan === 'enterprise1' || userPlan === 'enterprise2' || userPlan === 'enterprise3';

  const { sidebarWidth, setMobileTitleOverride } = useSidebar();

  // Set mobile header title via sidebar context
  useEffect(() => {
    setMobileTitleOverride('Video de influencer');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  // Close accent dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (accentDropdownRef.current && !accentDropdownRef.current.contains(e.target as Node)) {
        setAccentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch user plan for Modo Pro check
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoadingPlan(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) { setLoadingPlan(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', profile.id_company)
          .single();
        if (company?.plan) {
          setUserPlan(company.plan);
          if (company.plan !== 'negocio' && company.plan !== 'enterprise' && company.plan !== 'enterprise1' && company.plan !== 'enterprise2' && company.plan !== 'enterprise3') {
            setModoPro(false);
          }
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchUserPlan();
  }, []);

  // Influencer model state
  const [mode, setMode] = useState<ModeloMode>('ia');
  const [guardadoId, setGuardadoId] = useState<string>('');
  const [guardadoNombre, setGuardadoNombre] = useState<string>('');
  const [genero, setGenero] = useState<string>('');
  const [edad, setEdad] = useState<string>('');
  const [tipoCuerpo, setTipoCuerpo] = useState<string>('');
  const [tonoPiel, setTonoPiel] = useState<string>('');
  const [colorPelo, setColorPelo] = useState<string>('');
  const [largoPelo, setLargoPelo] = useState<string>('');
  const [descModelo, setDescModelo] = useState<string>('');

  // Dialogue state
  const [selectedAccent, setSelectedAccent] = useState<string>('argentino');
  const [accentDropdownOpen, setAccentDropdownOpen] = useState(false);
  const accentDropdownRef = useRef<HTMLDivElement>(null);

  // Modelo Personalizado menu states
  const [showPersonalizadoMenu, setShowPersonalizadoMenu] = useState(false);
  const [personalizadoMenuAnimated, setPersonalizadoMenuAnimated] = useState(false);
  const [personalizadoMenuDragY, setPersonalizadoMenuDragY] = useState(0);
  const personalizadoDragStartY = useRef<number | null>(null);

  // Inline video results state
  const [showResults, setShowResults] = useState(false);
  const [generatedVideoId, setGeneratedVideoId] = useState<string>('');
  const [companyIdForVideo, setCompanyIdForVideo] = useState<string>('');
  const willShowResultsRef = useRef<boolean>(false);

  const [dialogue, setDialogue] = useState<string>('');
  const [loadingDialogue, setLoadingDialogue] = useState<boolean>(true);
  const [dialogueError, setDialogueError] = useState<string | null>(null);

  // Saved models state
  type SavedModelItem = { id: number; nombre: string; fotoFileName?: string; signedUrl?: string };
  const [savedModels, setSavedModels] = useState<SavedModelItem[]>([]);
  const [loadingSavedModels, setLoadingSavedModels] = useState(false);
  const [savedModelsError, setSavedModelsError] = useState<string | null>(null);
  const [savedModelsFetched, setSavedModelsFetched] = useState(false);
  const [selectedSavedModelId, setSelectedSavedModelId] = useState<number | null>(null);

  // Add model modal state
  const [showAddModelCard, setShowAddModelCard] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadConsentA, setUploadConsentA] = useState(false);
  const [uploadConsentB, setUploadConsentB] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [companyId, setCompanyId] = useState<string | null>(null);

  // Video generation state
  const [generating, setGenerating] = useState<boolean>(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genModerationError, setGenModerationError] = useState<boolean>(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const consecutiveErrorCountRef = useRef<number>(0);
  const [showContactQR, setShowContactQR] = useState<boolean>(false);
  // Ref to block rapid clicks before React re-renders
  const generatingLockRef = useRef<boolean>(false);

  // Insufficient images modal state
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState<boolean>(false);
  const [requiredImagesCount, setRequiredImagesCount] = useState<number>(24);
  const [remainingImagesCount, setRemainingImagesCount] = useState<number>(0);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);

  const skinToneOptions = [
    { label: 'Muy clara', color: '#FFDFC4' },
    { label: 'Clara', color: '#F0C8A0' },
    { label: 'Beige', color: '#DEB887' },
    { label: 'Trigueña', color: '#CD853F' },
    { label: 'Oscura', color: '#8B4513' },
    { label: 'Muy oscura', color: '#3C2414' },
  ];
  const hairColorOptions = [
    { label: 'Negro', color: '#232323' },
    { label: 'Castaño oscuro', color: '#4B2E09' },
    { label: 'Castaño claro', color: '#A0522D' },
    { label: 'Rubio', color: '#F8E49B' },
    { label: 'Colorado', color: '#B55239' },
  ];

  const uploadPreviewUrl = useMemo(() => {
    if (!uploadFile) return '';
    try {
      return URL.createObjectURL(uploadFile);
    } catch {
      return '';
    }
  }, [uploadFile]);

  useEffect(() => {
    return () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    };
  }, [uploadPreviewUrl]);

  const resetUploadState = useCallback(() => {
    setUploadName('');
    setUploadFile(null);
    setUploadConsentA(false);
    setUploadConsentB(false);
    setUploadSaving(false);
    setUploadError(null);
  }, []);

  const ensureCompanyId = useCallback(async () => {
    if (companyId) return companyId;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No se encontró el usuario');
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    const idc = (profile as any)?.id_company as string | undefined;
    if (!idc) throw new Error('No se encontró la empresa del usuario');
    setCompanyId(idc);
    return idc;
  }, [companyId]);

  const fetchSavedModels = useCallback(async () => {
    try {
      setLoadingSavedModels(true);
      setSavedModelsError(null);
      const compId = await ensureCompanyId();
      const { data, error } = await supabase
        .from('modelos')
        .select('id, nombre, foto_file_name')
        .eq('id_company', compId)
        .order('nombre');
      if (error) throw error;
      const rows = (data as any[]) || [];
      const withSigned: SavedModelItem[] = await Promise.all(
        rows.map(async (m) => {
          let signedUrl: string | undefined;
          const fileName: string | undefined = m?.foto_file_name || undefined;
          if (fileName) {
            try {
              const { data: signed } = await supabase.storage
                .from('modelos')
                .createSignedUrl(`${compId}/full/${fileName}`, 3600);
              if (signed?.signedUrl) signedUrl = signed.signedUrl;
            } catch {
            }
          }
          return {
            id: m.id as number,
            nombre: m.nombre as string,
            fotoFileName: fileName,
            signedUrl,
          } as SavedModelItem;
        }),
      );
      setSavedModels(withSigned);
    } catch (e) {
      console.error(e);
      setSavedModelsError(
        e instanceof Error ? e.message : 'Error al cargar modelos guardados',
      );
    } finally {
      setLoadingSavedModels(false);
      setSavedModelsFetched(true);
    }
  }, [ensureCompanyId]);

  const handleUploadSave = useCallback(async () => {
    try {
      setUploadError(null);
      if (!uploadName.trim()) throw new Error('Ingresá un nombre para el modelo');
      if (!uploadFile) throw new Error('Subí una imagen del rostro');
      if (!uploadConsentA || !uploadConsentB) throw new Error('Debés aceptar ambas confirmaciones');
      setUploadSaving(true);
      const compId = await ensureCompanyId();
      const ext = (uploadFile.name.split('.').pop() || 'jpg').toLowerCase();
      const base = uploadName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const fileName = `${Date.now()}-${base || 'modelo'}.${ext}`;
      const targetPath = `${compId}/full/${fileName}`;
      const { error: upErr } = await supabase.storage
        .from('modelos')
        .upload(targetPath, uploadFile, { cacheControl: '3600', upsert: false, contentType: uploadFile.type || 'image/jpeg' });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase
        .from('modelos')
        .insert({ id_company: compId, nombre: uploadName.trim(), foto_file_name: fileName });
      if (insErr) throw insErr;
      await fetchSavedModels();
      setShowAddModelCard(false);
      resetUploadState();
      setUploadSaving(false);
    } catch (e: any) {
      console.error('Error subiendo modelo:', e);
      setUploadError(e?.message || 'No se pudo guardar el modelo');
      setUploadSaving(false);
    }
  }, [uploadName, uploadFile, uploadConsentA, uploadConsentB, ensureCompanyId, fetchSavedModels, resetUploadState]);

  useEffect(() => {
    if (mode === 'guardados' && !savedModelsFetched && !loadingSavedModels) {
      fetchSavedModels();
    }
  }, [mode, savedModelsFetched, loadingSavedModels, fetchSavedModels]);

  // Fetch dialogue on mount
  useEffect(() => {
    const fetchDialogue = async () => {
      try {
        setLoadingDialogue(true);
        setDialogueError(null);
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No se encontró el usuario');

        const variantId = ids[0] || '';
        if (!variantId) {
          setDialogue('');
          setLoadingDialogue(false);
          return;
        }

        const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_dialogo', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': GENERATE_AUTH_TOKEN 
          },
          body: JSON.stringify({ 
            user_id: user.id, 
            variante_id: variantId 
          })
        });

        if (!response.ok) {
          throw new Error('Error al generar el diálogo');
        }

        const data = await response.json();
        setDialogue(data?.response || '');
      } catch (e) {
        console.error('Error fetching dialogue:', e);
        setDialogueError(e instanceof Error ? e.message : 'Error al generar el diálogo');
      } finally {
        setLoadingDialogue(false);
      }
    };

    fetchDialogue();
  }, [ids]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleGenerateVideo = async () => {
    // Immediate lock to prevent rapid clicks
    if (generatingLockRef.current) return;
    generatingLockRef.current = true;

    if (ids.length === 0 || loadingDialogue || generating) {
      generatingLockRef.current = false;
      return;
    }

    try {
      setGenError(null);
      setGenModerationError(false);
      setGenerating(true);
      setCompleteProgressNow(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');
      const compId = profile.id_company as string;

      // Check image quota before generating video
      const requiredImages = modoPro ? 72 : 24;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
        .eq('id_company', compId)
        .single();

      const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining < requiredImages) {
        setRequiredImagesCount(requiredImages);
        setRemainingImagesCount(remaining);
        // Compute renewal date
        const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
        const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
        if (paidStart) {
          const now = new Date();
          const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
          const anchorDay = ref.getDate();
          const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
          const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
          let start = anchorFor(now.getFullYear(), now.getMonth());
          if (start.getTime() > now.getTime()) {
            const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            start = anchorFor(prev.getFullYear(), prev.getMonth());
          }
          const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
          setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
        } else {
          setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
        }
        setShowInsufficientImagesModal(true);
        setGenerating(false);
        generatingLockRef.current = false;
        return;
      }

      // Build request payload
      const variantIds = ids.map(s => String(s).trim()).filter(s => !!s && s !== '0');
      if (variantIds.length === 0) throw new Error('No hay productos válidos seleccionados');

      // Get principal image of the first selected product (or any image if no principal)
      let imageWidth: number | undefined;
      let imageHeight: number | undefined;
      console.log('[ConfigurarVideoInfluencer] Starting image dimension fetch for variantId:', variantIds[0], 'compId:', compId);
      try {
        // First try to get the principal image
        let { data: firstImageData, error: imgQueryError } = await supabase
          .from('productos_imagenes')
          .select('id, url, variante_id, principal')
          .eq('variante_id', variantIds[0])
          .eq('id_company', compId)
          .eq('principal', true)
          .limit(1)
          .single();

        console.log('[ConfigurarVideoInfluencer] Principal image query result:', { firstImageData, imgQueryError });

        // If no principal image found, get any image from the product
        if (!firstImageData) {
          const { data: anyImageData, error: anyImgError } = await supabase
            .from('productos_imagenes')
            .select('id, url, variante_id, principal')
            .eq('variante_id', variantIds[0])
            .eq('id_company', compId)
            .limit(1)
            .single();
          
          firstImageData = anyImageData;
          imgQueryError = anyImgError;
          console.log('[ConfigurarVideoInfluencer] Fallback image query result:', { firstImageData, imgQueryError });
        }

        if (firstImageData?.url) {
          // Get signed URL for the image
          const imagePath = `${compId}/${variantIds[0]}/full/${firstImageData.url}`;
          console.log('[ConfigurarVideoInfluencer] Image path for signed URL:', imagePath);
          
          const { data: signedData, error: signedError } = await supabase.storage
            .from('productos')
            .createSignedUrl(imagePath, 60);

          console.log('[ConfigurarVideoInfluencer] Signed URL result:', { signedUrl: signedData?.signedUrl, signedError });

          if (signedData?.signedUrl) {
            // Load image to get dimensions
            console.log('[ConfigurarVideoInfluencer] Loading image to get dimensions...');
            const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                console.log('[ConfigurarVideoInfluencer] Image loaded successfully, naturalWidth:', img.naturalWidth, 'naturalHeight:', img.naturalHeight);
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
              };
              img.onerror = (err) => {
                console.error('[ConfigurarVideoInfluencer] Image load error:', err);
                reject(new Error('Failed to load image'));
              };
              img.src = signedData.signedUrl;
            });
            imageWidth = dimensions.width;
            imageHeight = dimensions.height;
            console.log('[ConfigurarVideoInfluencer] Final image dimensions:', { width: imageWidth, height: imageHeight });
          } else {
            console.warn('[ConfigurarVideoInfluencer] No signed URL returned');
          }
        } else {
          console.warn('[ConfigurarVideoInfluencer] No image data found or no URL in image data');
        }
      } catch (imgErr) {
        console.warn('[ConfigurarVideoInfluencer] Could not get image dimensions:', imgErr);
        // Continue without dimensions - they are optional
      }

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode: variantIds.length > 1 ? 'multiple' : 'single',
        tipo_video: 'influencer',
        dialogo: dialogue.trim(),
        acento: selectedAccent,
        modelo_mode: mode,
        modo_pro: modoPro,
      };

      // Add image dimensions if available
      if (imageWidth && imageHeight) {
        requestData.image_width = imageWidth;
        requestData.image_height = imageHeight;
      }

      // Add model customization options based on mode
      if (mode === 'guardados' && guardadoId) {
        requestData.modelo_guardado_id = guardadoId;
        if (guardadoNombre) requestData.modelo_guardado_nombre = guardadoNombre;
      } else if (mode === 'personalizado') {
        if (genero) requestData.genero = genero;
        if (edad) requestData.edad = edad;
        if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
        if (tonoPiel) requestData.tono_piel = tonoPiel;
        if (colorPelo) requestData.color_pelo = colorPelo;
        if (largoPelo) requestData.largo_pelo = largoPelo;
        if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
      }

      console.log('[ConfigurarVideoInfluencer] handleGenerateVideo payload', requestData);

      const response = await fetch(GENERATE_VIDEO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.text(); } catch {}
      console.log('[ConfigurarVideoInfluencer] handleGenerateVideo response', { status: response.status, ok: response.ok, text: respText });

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = respText ? JSON.parse(respText) : null; } catch (e) { console.error('[ConfigurarVideoInfluencer] json parse error', e); }

      // Expect response: { response: 'success', video_generado_id: 'uuid-string' } or { response: 'success', video_id: 'uuid-string' }
      if (data?.response === 'success' && (data?.video_generado_id || data?.video_id)) {
        const videoId = (data.video_generado_id || data.video_id) as string;
        willShowResultsRef.current = true;
        setGeneratedVideoId(videoId);
        setCompanyIdForVideo(compId);
        setCompleteProgressNow(true);
        consecutiveErrorCountRef.current = 0;
      } else if (data?.response === 'error_moderation') {
        consecutiveErrorCountRef.current += 1;
        setGenModerationError(true);
        setGenerating(false);
        generatingLockRef.current = false;
        return;
      } else {
        throw new Error('No se recibió el video generado.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoInfluencer] handleGenerateVideo error', e);
      consecutiveErrorCountRef.current += 1;
      setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      setGenerating(false);
      generatingLockRef.current = false;
    }
  };

  // Personalizado menu animation
  useEffect(() => {
    if (showPersonalizadoMenu) {
      requestAnimationFrame(() => setPersonalizadoMenuAnimated(true));
    }
  }, [showPersonalizadoMenu]);

  const closePersonalizadoMenu = () => {
    setPersonalizadoMenuAnimated(false);
    setTimeout(() => setShowPersonalizadoMenu(false), 300);
  };

  const handlePersonalizadoTouchStart = (e: React.TouchEvent) => {
    personalizadoDragStartY.current = e.touches[0].clientY;
  };
  const handlePersonalizadoTouchMove = (e: React.TouchEvent) => {
    if (personalizadoDragStartY.current === null) return;
    const dy = e.touches[0].clientY - personalizadoDragStartY.current;
    if (dy > 0) setPersonalizadoMenuDragY(dy);
  };
  const handlePersonalizadoTouchEnd = () => {
    if (personalizadoMenuDragY > 100) {
      closePersonalizadoMenu();
    }
    setPersonalizadoMenuDragY(0);
    personalizadoDragStartY.current = null;
  };

  const tapFlash = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    el.classList.remove('tap-flash');
    void el.offsetWidth;
    el.classList.add('tap-flash');
  };

  if (showResults) {
    return (
      <GeneratedImagesInline
        imageIds={[generatedVideoId]}
        companyId={companyIdForVideo}
        onClose={() => {
          setShowResults(false);
        }}
        onReturnToConfig={() => {
          setShowResults(false);
        }}
        onReady={() => {}}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] md:min-h-screen bg-[#F2F2F7]">
      {/* Desktop sticky header — PruebaNuevaInterfaz style */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="bg-white border-b border-black/[0.06]">
          <div className="max-w-4xl mx-auto px-8">
            <div className="flex items-center justify-between h-14">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold text-brand-black/90">Video de influencer</h1>
              <button
                type="button"
                onClick={handleGenerateVideo}
                disabled={loadingDialogue || generating}
                className={`inline-flex items-center justify-center gap-2 px-5 h-9 rounded-[10px] text-[15px] font-medium transition-all ${
                  (loadingDialogue || generating)
                    ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                    : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
                }`}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : 'Generar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-1 pb-28 sm:px-8 sm:pt-[72px] sm:pb-6">

        {/* Tipo de influencer — iOS grouped list */}
        <div className="mt-2 sm:mt-4">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Tipo de influencer</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
            {/* Automático */}
            <button
              type="button"
              onClick={() => setMode('ia')}
              onClickCapture={tapFlash}
              className="w-full px-4 py-[11px] text-[15px] transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <span className={`material-symbols-rounded text-[20px] shrink-0 ${mode === 'ia' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>auto_awesome</span>
                <span className="text-left">
                  <span className="text-[15px] text-brand-black/90">Automático</span>
                  <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">La IA elige un influencer apropiado para tu producto</span>
                </span>
              </span>
              {mode === 'ia' && <Check className="w-5 h-5 text-[#1644EB] shrink-0" />}
            </button>

            <div className="ml-[46px] h-px bg-black/[0.08]" />

            {/* Personalizado */}
            <button
              type="button"
              onClick={() => {
                setMode('personalizado');
                setShowPersonalizadoMenu(true);
              }}
              onClickCapture={tapFlash}
              className="w-full px-4 py-[11px] text-[15px] transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-3">
                <span className={`material-symbols-rounded text-[20px] shrink-0 ${mode === 'personalizado' ? 'text-[#1644EB]' : 'text-neutral-400'}`}>edit</span>
                <span className="text-left">
                  <span className="text-[15px] text-brand-black/90">Personalizado</span>
                  <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">Definí las características del influencer</span>
                </span>
              </span>
              {mode === 'personalizado' ? <Check className="w-5 h-5 text-[#1644EB] shrink-0" /> : <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC] shrink-0" />}
            </button>
          </div>
        </div>

        {/* Diálogo — iOS grouped list */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Diálogo</p>

          {/* Accent row — custom dropdown */}
          <div ref={accentDropdownRef} className="relative z-10">
            <div className="rounded-[13px] bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              <button
                type="button"
                onClick={() => setAccentDropdownOpen(!accentDropdownOpen)}
                className="w-full px-4 py-[11px] flex items-center justify-between rounded-[13px]"
              >
                <span className="flex items-center gap-3">
                  <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">language</span>
                  <span className="text-[15px] text-brand-black/90">Acento</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-[15px] text-[#3C3C43]/60">{accentOptions.find(o => o.value === selectedAccent)?.label}</span>
                  <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
                </span>
              </button>
            </div>
            {accentDropdownOpen && (
              <div className="absolute z-50 mt-1 right-0 min-w-[180px] max-h-60 overflow-y-auto bg-white border border-neutral-200 rounded-md shadow-md py-1">
                {accentOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => { setSelectedAccent(option.value); setAccentDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-[15px] flex items-center justify-between transition-colors ${
                      selectedAccent === option.value ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-brand-black/70 hover:bg-neutral-100'
                    }`}
                  >
                    <span>{option.label}</span>
                    {selectedAccent === option.value && <Check className="w-[18px] h-[18px] text-[#1644EB] shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dialogue content — iOS card */}
          <div className="mt-3">
            {loadingDialogue ? (
              <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
                <div className="flex items-center gap-2 mb-3">
                  <Loader2 className="w-5 h-5 text-[#1644EB] animate-spin" />
                  <span className="text-[15px] text-[#3C3C43]/60">Generando diálogo...</span>
                </div>
                <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#1644EB] rounded-full"
                    style={{ animation: 'dialogueProgress 15s linear forwards' }}
                  />
                </div>
                <style>{`
                  @keyframes dialogueProgress {
                    from { width: 0%; }
                    to { width: 100%; }
                  }
                `}</style>
              </div>
            ) : dialogueError ? (
              <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                <p className="text-[15px] text-[#FF3B30]">{dialogueError}</p>
              </div>
            ) : (
              <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
                <p className="text-[13px] text-[#3C3C43]/60 mb-2">Este es el texto que dirá el influencer en el video.</p>
                <textarea
                  rows={6}
                  value={dialogue}
                  onChange={(e) => setDialogue(e.target.value)}
                  placeholder="El diálogo aparecerá aquí..."
                  className="w-full rounded-[10px] border-none bg-black/[0.04] p-3 text-[15px] text-brand-black/90 placeholder:text-brand-black/25 focus:outline-none focus:ring-0 resize-none"
                />
                {dialogue.length > 170 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
                    <Info className="w-4 h-4 text-[#FF9500] shrink-0" />
                    <span className="text-[13px] text-[#3C3C43]/60">
                      La duración del video será de 12 segundos. Si el diálogo es muy largo solo se dirá parte de este.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Calidad de video — Modo Pro toggle */}
        <div className="mt-6">
          <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Calidad de video</p>
          <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)] relative">
            <div className="px-4 py-[11px] flex items-center justify-between">
              <span className="flex items-center gap-3 flex-1 min-w-0">
                <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">high_quality</span>
                <span className="text-left min-w-0">
                  <span className="text-[15px] text-brand-black/90 flex items-center gap-2">
                    Modo Pro
                    {!loadingPlan && !isProPlanAllowed && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[6px] text-[11px] font-medium bg-black/[0.05] text-[#3C3C43]/40">
                        <Lock className="w-3 h-3" />
                        Plan Pro / Ultra
                      </span>
                    )}
                  </span>
                  <span className="block text-[13px] text-[#3C3C43]/60 mt-0.5">Mayor calidad y definición</span>
                </span>
              </span>
              <label className="relative inline-flex cursor-pointer shrink-0 ml-3">
                <input
                  type="checkbox"
                  checked={modoPro && isProPlanAllowed}
                  onChange={() => {
                    if (isProPlanAllowed) {
                      setModoPro(!modoPro);
                    } else if (!loadingPlan) {
                      setShowProPlanModal(true);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className={`w-[51px] h-[31px] rounded-full transition-colors ${modoPro && isProPlanAllowed ? 'bg-[#1644EB]' : 'bg-black/[0.09]'}`} />
                <div className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform ${modoPro && isProPlanAllowed ? 'translate-x-[20px]' : 'translate-x-0'}`} />
              </label>
            </div>
            {isProPlanAllowed && modoPro && (
              <>
                <div className="ml-[46px] h-px bg-black/[0.08]" />
                <div className="px-4 py-[11px] flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                  <span className="text-[13px] text-[#3C3C43]/60">Un video en Modo Pro consume 72 imágenes.</span>
                </div>
              </>
            )}
          </div>

          {/* Pro Plan Popover */}
          {showProPlanModal && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowProPlanModal(false)} />
              <div className="relative z-50 mt-2 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-[8px] bg-black/[0.05] flex items-center justify-center">
                    <Lock className="w-[18px] h-[18px] text-[#3C3C43]/40" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[15px] font-semibold text-brand-black/90">Función exclusiva</h4>
                    <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                      El Modo Pro está disponible solo para los planes Pro y Ultra.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowProPlanModal(false); navigate('/subscription'); }}
                  className="mt-4 w-full text-[15px] font-semibold text-[#1644EB] py-2 text-center hover:opacity-70 transition-opacity"
                >
                  Ver planes
                </button>
              </div>
            </>
          )}
        </div>

      </div>

      {/* Mobile bottom bar — frosted glass */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        {loadingDialogue ? (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60 mb-2">
            <Loader2 className="w-3.5 h-3.5 text-[#1644EB] animate-spin" />
            <span>Generando diálogo...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60 mb-2">
            <Info className="w-3.5 h-3.5 text-[#1644EB]" />
            <span>Se consumirán {modoPro ? '72' : '24'} imágenes</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleGenerateVideo}
          disabled={loadingDialogue || generating}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            (loadingDialogue || generating) ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed' : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {generating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </span>
          ) : 'Generar video'}
        </button>
      </div>

      {/* Add Model Modal — iOS sheet */}
      {showAddModelCard && (
        <div className="fixed inset-0 z-[100000] bg-brand-black/40 backdrop-blur-sm flex flex-col sm:items-center sm:justify-center">
          <div className="bg-[#F2F2F7] sm:bg-white w-full h-full sm:h-auto sm:max-w-md sm:rounded-[13px] sm:shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] overflow-hidden flex flex-col">
            {/* Sheet header */}
            <div className="px-4 pt-4 pb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setShowAddModelCard(false); resetUploadState(); }}
                className="text-[15px] text-[#1644EB] active:opacity-50"
              >
                Cancelar
              </button>
              <h3 className="text-[17px] font-semibold text-brand-black/90 absolute left-1/2 -translate-x-1/2">Añadir modelo</h3>
              <button
                type="button"
                onClick={handleUploadSave}
                disabled={!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving}
                className={`text-[15px] font-semibold ${(!uploadName.trim() || !uploadFile || !uploadConsentA || !uploadConsentB || uploadSaving) ? 'text-[#3C3C43]/30 cursor-not-allowed' : 'text-[#1644EB] active:opacity-50'}`}
              >
                {uploadSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-8">
              {uploadError && (
                <div className="bg-[#FF3B30]/10 rounded-[10px] px-4 py-3 mb-4">
                  <p className="text-[13px] text-[#FF3B30]">{uploadError}</p>
                </div>
              )}

              {/* Name field — iOS grouped list */}
              <div className="mt-2">
                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Nombre</p>
                <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder="Ej: Juan Pérez"
                    className="w-full px-4 py-[11px] bg-transparent text-[15px] text-brand-black/90 placeholder:text-brand-black/25 focus:outline-none"
                  />
                </div>
              </div>

              {/* Image upload */}
              <div className="mt-5">
                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Imagen</p>
                <div className="rounded-[13px] bg-white p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
                  <p className="text-[13px] text-[#3C3C43]/60 mb-3">
                    Subí una imagen del rostro, de frente, desde los hombros hacia arriba, con buena iluminación.
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-[10px] bg-black/[0.03] flex items-center justify-center overflow-hidden shrink-0">
                      {uploadPreviewUrl ? (
                        <img src={uploadPreviewUrl} alt="Vista previa" className="w-full h-full object-cover" />
                      ) : (
                        <span className="material-symbols-rounded text-[28px] text-brand-black/20">image</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        id="upload-model-file-video"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                      {!uploadPreviewUrl && (
                        <label
                          htmlFor="upload-model-file-video"
                          className="inline-flex items-center gap-2 px-4 h-9 rounded-[8px] text-[15px] font-medium cursor-pointer bg-[#1644EB] text-white active:bg-[#1238C8] transition-colors"
                        >
                          <ImagePlus className="w-4 h-4" />
                          Subir imagen
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Consent checkboxes */}
              <div className="mt-5">
                <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Consentimiento</p>
                <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
                  <label className="flex items-start gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-[18px] w-[18px] rounded-[4px] border-black/[0.15] accent-[#1644EB]"
                      checked={uploadConsentA}
                      onChange={(e) => setUploadConsentA(e.target.checked)}
                    />
                    <span className="text-[13px] text-brand-black/70 leading-[18px]">
                      Confirmo que esta persona accedió a que yo utilice su imagen, y dio su consentimiento para que se generaran imágenes con su rostro.
                    </span>
                  </label>
                  <div className="ml-[42px] h-px bg-black/[0.08]" />
                  <label className="flex items-start gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-[18px] w-[18px] rounded-[4px] border-black/[0.15] accent-[#1644EB]"
                      checked={uploadConsentB}
                      onChange={(e) => setUploadConsentB(e.target.checked)}
                    />
                    <span className="text-[13px] text-brand-black/70 leading-[18px]">
                      Acepto toda responsabilidad legal relacionada al uso de la imagen de esta persona para la generación de imágenes en Atlas.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Animation */}
      {generating && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar unos minutos. No cierres esta ventana."
          durationMs={240000}
          completeNow={completeProgressNow}
          showCountdown
          onCompleted={() => {
            if (willShowResultsRef.current) {
              willShowResultsRef.current = false;
              setShowResults(true);
            }
            setGenerating(false);
            generatingLockRef.current = false;
          }}
        />
      )}

      {/* Error toast — iOS card style */}
      {genError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Error al generar el video</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
              {consecutiveErrorCountRef.current >= 2 && (
                <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                  Si el problema persiste,{' '}
                  <a
                    href="https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20un%20video%20en%20Atlas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1644EB] underline sm:hidden"
                  >
                    contactanos
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowContactQR(true)}
                    className="text-[#1644EB] underline hidden sm:inline"
                  >
                    contactanos
                  </button>
                </p>
              )}
            </div>
            <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Moderation error toast */}
      {genModerationError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">block</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">No se puede generar videos con este tipo de productos</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">No se consumieron imágenes.</p>
              {consecutiveErrorCountRef.current >= 2 && (
                <p className="text-[13px] text-[#3C3C43]/60 mt-1">
                  Si el problema persiste,{' '}
                  <a
                    href="https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20un%20video%20en%20Atlas"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1644EB] underline sm:hidden"
                  >
                    contactanos
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowContactQR(true)}
                    className="text-[#1644EB] underline hidden sm:inline"
                  >
                    contactanos
                  </button>
                </p>
              )}
            </div>
            <button type="button" onClick={() => setGenModerationError(false)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Insufficient Images — matches SubscriptionLimitModal style */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para generar video
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para generar este video necesitás <strong>{requiredImagesCount} imágenes</strong>, pero {remainingImagesCount === 0 ? <>no te quedan imágenes restantes este mes</> : <>solo tenés <strong>{remainingImagesCount}</strong> restantes este mes</>}.
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para generar videos ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowInsufficientImagesModal(false); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setShowInsufficientImagesModal(false)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact QR Modal - Desktop only */}
      {showContactQR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6 relative">
            <button
              onClick={() => setShowContactQR(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-neutral-900 mb-2 text-center">Contactanos por WhatsApp</h3>
            <p className="text-[15px] text-neutral-600 mb-4 text-center">Escaneá el código QR con tu celular para abrir WhatsApp</p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://wa.me/5491122635392?text=Hola%2C%20tuve%20un%20error%20al%20generar%20un%20video%20en%20Atlas')}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48"
              />
            </div>
            <button
              onClick={() => setShowContactQR(false)}
              className="w-full py-2 px-4 bg-neutral-100 text-neutral-700 rounded-full hover:bg-neutral-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Modelo Personalizado Menu */}
      {showPersonalizadoMenu && (
        <>
          {/* Desktop backdrop */}
          <div 
            className="hidden sm:block fixed inset-0 bg-black/40 z-[45] cursor-pointer"
            onClick={closePersonalizadoMenu}
          />
          {/* Desktop Side Panel */}
          <div 
            className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[50] flex-col overflow-hidden transition-transform duration-300 ease-out ${
              personalizadoMenuAnimated ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <h3 className="text-[17px] font-semibold text-brand-black/90">Influencer personalizado</h3>
              <button type="button" onClick={closePersonalizadoMenu} className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mx-5 h-px bg-black/[0.08]" />
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="text-[15px] text-neutral-500 mb-4">Definí las características del influencer para que se ajuste al estilo de tu marca.</p>
              <div className="space-y-5">
                {/* Género */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Género</label>
                  <div className="flex gap-2">
                    {['Hombre', 'Mujer'].map((g) => (
                      <button key={g} type="button" onClick={() => setGenero(genero === g ? '' : g)} className={`px-4 h-10 rounded-full border text-[15px] ${genero === g ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{g}</button>
                    ))}
                  </div>
                </div>
                {/* Edad */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Edad</label>
                  <div className="flex flex-wrap gap-2">
                    {['Joven (25 años)', 'Adulto/a(30 a 40 años)', 'Mayor'].map((e) => (
                      <button key={e} type="button" onClick={() => setEdad(edad === e ? '' : e)} className={`px-4 h-10 rounded-full border text-[15px] ${edad === e ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{e}</button>
                    ))}
                  </div>
                </div>
                {/* Tipo de cuerpo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Tipo de cuerpo</label>
                  <div className="flex flex-wrap gap-2">
                    {['Promedio', 'Talle Grande', 'Talle Extra Grande'].map((c) => (
                      <button key={c} type="button" onClick={() => setTipoCuerpo(tipoCuerpo === c ? '' : c)} className={`px-4 h-10 rounded-full border text-[15px] ${tipoCuerpo === c ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{c}</button>
                    ))}
                  </div>
                </div>
                {/* Tono de piel */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Tono de piel</label>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => setTonoPiel('')} className={`px-4 h-10 rounded-full border text-[15px] ${tonoPiel === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                    {skinToneOptions.map(({ label, color }) => (
                      <button key={label} type="button" title={label} onClick={() => setTonoPiel(tonoPiel === label ? '' : label)} className={`w-9 h-9 rounded-full border ${tonoPiel === label ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary' : 'border-neutral-300'} flex items-center justify-center`} style={{ backgroundColor: color }}>
                        {tonoPiel === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Color de pelo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Color de pelo</label>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={() => setColorPelo('')} className={`px-4 h-10 rounded-full border text-[15px] ${colorPelo === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                    {hairColorOptions.map(({ label, color }) => (
                      <button key={label} type="button" title={label} onClick={() => setColorPelo(colorPelo === label ? '' : label)} className={`w-9 h-9 rounded-full border ${colorPelo === label ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary' : 'border-neutral-300'} flex items-center justify-center`} style={{ backgroundColor: color }}>
                        {colorPelo === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Largo de pelo */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Largo de pelo</label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setLargoPelo('')} className={`px-4 h-10 rounded-full border text-[15px] ${largoPelo === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                    {['Rapado', 'Corto', 'Medio', 'Largo'].map((l) => (
                      <button key={l} type="button" onClick={() => setLargoPelo(largoPelo === l ? '' : l)} className={`px-4 h-10 rounded-full border text-[15px] ${largoPelo === l ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{l}</button>
                    ))}
                  </div>
                </div>
                {/* Descripción adicional */}
                <div>
                  <label className="block text-[15px] text-neutral-700 mb-2">Descripción adicional</label>
                  <textarea rows={3} value={descModelo} onChange={(e) => setDescModelo(e.target.value)} placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados" className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary" />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 flex justify-end gap-3">
              <button type="button" onClick={closePersonalizadoMenu} className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors">Cancelar</button>
              <button type="button" onClick={closePersonalizadoMenu} className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors">Aceptar</button>
            </div>
          </div>
          {/* Mobile Bottom Sheet */}
          {ReactDOM.createPortal(
            <>
              <div 
                className={`sm:hidden fixed inset-0 z-[200] bg-black/40 transition-all duration-300 ${personalizadoMenuAnimated ? 'opacity-100' : 'opacity-0'}`} 
                onClick={closePersonalizadoMenu} 
              />
              <div 
                className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
                style={{ 
                  height: 'calc(100dvh - 1rem)', 
                  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                  transform: personalizadoMenuDragY > 0 
                    ? `translateY(${personalizadoMenuDragY}px)` 
                    : personalizadoMenuAnimated ? 'translateY(0)' : 'translateY(100%)',
                  transition: personalizadoMenuDragY === 0 ? 'transform 0.3s ease-out' : 'none'
                }}
              >
                <div 
                  className="py-2 cursor-grab active:cursor-grabbing"
                  onTouchStart={handlePersonalizadoTouchStart}
                  onTouchMove={handlePersonalizadoTouchMove}
                  onTouchEnd={handlePersonalizadoTouchEnd}
                >
                  <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
                </div>
                <div className="flex items-center justify-between px-4 pb-3">
                  <button type="button" onClick={closePersonalizadoMenu} className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity w-20 text-left">Cancelar</button>
                  <h3 className="text-[15px] font-semibold text-brand-black/90">Influencer personalizado</h3>
                  <button type="button" onClick={closePersonalizadoMenu} className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity w-20 text-right">Aceptar</button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  <p className="text-[15px] text-neutral-500 mb-4">Definí las características del influencer para que se ajuste al estilo de tu marca.</p>
                  <div className="space-y-5">
                    {/* Género */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Género</label>
                      <div className="flex gap-2">
                        {['Hombre', 'Mujer'].map((g) => (
                          <button key={g} type="button" onClick={() => setGenero(genero === g ? '' : g)} className={`px-4 h-10 rounded-full border text-[15px] ${genero === g ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{g}</button>
                        ))}
                      </div>
                    </div>
                    {/* Edad */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Edad</label>
                      <div className="flex flex-wrap gap-2">
                        {['Joven (25 años)', 'Adulto/a(30 a 40 años)', 'Mayor'].map((e) => (
                          <button key={e} type="button" onClick={() => setEdad(edad === e ? '' : e)} className={`px-4 h-10 rounded-full border text-[15px] ${edad === e ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{e}</button>
                        ))}
                      </div>
                    </div>
                    {/* Tipo de cuerpo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Tipo de cuerpo</label>
                      <div className="flex flex-wrap gap-2">
                        {['Promedio', 'Talle Grande', 'Talle Extra Grande'].map((c) => (
                          <button key={c} type="button" onClick={() => setTipoCuerpo(tipoCuerpo === c ? '' : c)} className={`px-4 h-10 rounded-full border text-[15px] ${tipoCuerpo === c ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{c}</button>
                        ))}
                      </div>
                    </div>
                    {/* Tono de piel */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Tono de piel</label>
                      <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={() => setTonoPiel('')} className={`px-4 h-10 rounded-full border text-[15px] ${tonoPiel === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                        {skinToneOptions.map(({ label, color }) => (
                          <button key={label} type="button" title={label} onClick={() => setTonoPiel(tonoPiel === label ? '' : label)} className={`w-9 h-9 rounded-full border ${tonoPiel === label ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary' : 'border-neutral-300'} flex items-center justify-center`} style={{ backgroundColor: color }}>
                            {tonoPiel === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Color de pelo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Color de pelo</label>
                      <div className="flex gap-2 flex-wrap">
                        <button type="button" onClick={() => setColorPelo('')} className={`px-4 h-10 rounded-full border text-[15px] ${colorPelo === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                        {hairColorOptions.map(({ label, color }) => (
                          <button key={label} type="button" title={label} onClick={() => setColorPelo(colorPelo === label ? '' : label)} className={`w-9 h-9 rounded-full border ${colorPelo === label ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary' : 'border-neutral-300'} flex items-center justify-center`} style={{ backgroundColor: color }}>
                            {colorPelo === label && <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Largo de pelo */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Largo de pelo</label>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setLargoPelo('')} className={`px-4 h-10 rounded-full border text-[15px] ${largoPelo === '' ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>Cualquiera</button>
                        {['Rapado', 'Corto', 'Medio', 'Largo'].map((l) => (
                          <button key={l} type="button" onClick={() => setLargoPelo(largoPelo === l ? '' : l)} className={`px-4 h-10 rounded-full border text-[15px] ${largoPelo === l ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    {/* Descripción adicional */}
                    <div>
                      <label className="block text-[15px] text-neutral-700 mb-2">Descripción adicional</label>
                      <textarea rows={3} value={descModelo} onChange={(e) => setDescModelo(e.target.value)} placeholder="Ej: rulos, sonriendo, ojos celestes, brazos cruzados" className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
        </>
      )}
    </div>
  );
};

export default ConfigurarVideoInfluencer;
